import localStorage from 'Browser/localStorage';
import validateEmail from 'Util/validateEmail';

import { AppController } from './AppController';
import {
  ConfCallController,
  OUT_GAIN_MIN,
  OUT_GAIN_MAX,
} from './ConfCallController';
import { TestCallController } from './TestCallController';
import Exceptions from './Exceptions';

function getStoredVolume() {
  if (localStorage.webCallConfVolume) {
    const val = parseInt(localStorage.webCallConfVolume, 10);
    if (!isNaN(val) && val >= OUT_GAIN_MIN && val <= OUT_GAIN_MAX)
      return val;
  }

  return 0;
}

export class ConfAppController extends AppController {
  constructor({
    localStorageKey = null,
    storedFields = null,
    defaultConferenceID = null,
    defaultFromName = null,
    defaultEmail = null,
    userIDEnabled = false,
    userIDRequired = false,
    alwaysListenOnly = false,
    enableTestApp = false,
    enableSettings = true,
    disableCommandButtons = false,
    hideHostControls = false,
  }) {
    super({
      localStorageKey,
      storedFields: storedFields || [
        'conferenceID',
        'fromName',
        'email',
        'userID',
      ],

      alwaysListenOnly,
      enableSettings,
    });

    this._defaultConferenceID = defaultConferenceID;
    this._defaultFromName = defaultFromName;
    this._defaultEmail = defaultEmail;
    this._userIDEnabled = userIDEnabled;
    this._userIDRequired = userIDRequired;
    this._enableTestApp = enableTestApp;
    this._disableCommandButtons = disableCommandButtons;

    this.callController = new ConfCallController({
      instanceName: 'ConfApp',
      hideHostControls,
    });
    this.addCallController('main', this.callController);

    this.testCallController = new TestCallController({
      instanceName: 'TestApp',
    });
    this.addCallController('test', this.testCallController);

    this.callController.on('update', () => this.emit('update'));
    this.testCallController.on('update', () => this.emit('update'));
  }

  get defaultConferenceID() {
    return this._defaultConferenceID;
  }

  get defaultFromName() {
    return this._defaultFromName;
  }

  get defaultEmail() {
    return this._defaultEmail;
  }

  get userIDEnabled() {
    return this._userIDEnabled;
  }

  set userIDEnabled(val) {
    if (this._userIDEnabled === val)
      return;

    this._userIDEnabled = !!val;
    this.emit('update');
  }

  get userIDRequired() {
    return this._userIDRequired;
  }

  set userIDRequired(val) {
    if (this._userIDRequired === val)
      return;

    this._userIDRequired = !!val;
    // userIDRequired is only used internally, no update required
  }

  get enableTestApp() {
    return this._enableTestApp;
  }

  get disableCommandButtons() {
    return this._disableCommandButtons;
  }

  processFormData(callType, formData) {
    let authString = '';
    let fromName = null;
    let email = 'caller@invalid';

    if (callType === 'main') {
      let conferenceID;
      if (this._getConferenceID) {
        // used by embedded
        conferenceID = this._getConferenceID();
      } else {
        // used by standalone
        if (this._defaultConferenceID !== null) {
          conferenceID = this._defaultConferenceID;
        } else {
          conferenceID = formData.conferenceID.replace(/[^\d#*]/g, '');

          if (!conferenceID.length)
            throw new Exceptions.ConnectionError('ERR_INVALID_CONFERENCE_ID');
        }
      }

      if (this._defaultFromName !== null) {
        fromName = this._defaultFromName;
      } else if (formData.fromName) {
        fromName = formData.fromName;
      }

      if (this._defaultEmail !== null) {
        email = this._defaultEmail;
      } else if (formData.email) {
        if (!validateEmail(formData.email)) {
          throw new Exceptions.ConnectionError('ERR_INVALID_EMAIL');
        }

        email = formData.email;
      }

      let userID;
      if (this._userIDEnabled) {
        userID = formData.userID;

        if (userID && !userID.match(/^\d+$/))
          throw new Exceptions.ConnectionError('ERR_INVALID_USERID');

        if (!userID && this._userIDRequired)
          throw new Exceptions.ConnectionError('ERR_INVALID_USERID');
      }

      authString = conferenceID;
      if (userID)
        authString += `#${userID}`;
    }

    const appConfig = this._debugMode ? formData : this._appConfig;
    return {
      wsSipURI: appConfig.wsSipURI,
      fromName,
      fromSipURI: `sip:${email}`,
      toSipURI: `${appConfig.toUriPrefix}${authString}${appConfig.toUriSuffix}`,

      fetchLocationURL: appConfig.fetchLocationURL,
      fetchRTCConfigurationURL: appConfig.fetchRTCConfigurationURL,
    };
  }

  processConnectParams(callType, connectParams) {
    connectParams.outGain = getStoredVolume();
    connectParams.toSipURI.setParam('outGain', connectParams.outGain);

    if (callType === 'test') {
      connectParams.toSipURI.setParam('callTest', '1');
    }
  }
}
